/**
 * NotificationRow — Surface Layer (/01).
 *
 * A single, presentational notification row. Modeled on the IBM Products
 * `NotificationsPanel` row pattern (Carbon for IBM Products), composed here
 * locally from `@carbon/react` primitives (ClickableTile + Tag) so it stays
 * Carbon-token compliant, dependency-light and trivially testable.
 *
 * Strictly presentational: it takes typed props and renders only client-safe
 * fields (title, optional body, a status Tag and a timestamp). It never renders
 * trace data, secrets, storage URIs or any internal path content.
 *
 * Optionally accepts an `actions` slot (a Carbon OverflowMenu + role-gated
 * Approve/Reject/Run/Delegate-up buttons). When supplied, the row is rendered
 * as a plain Tile (not Clickable) to avoid click-vs-action conflicts.
 */

import { type ReactNode } from 'react';
import { ClickableTile, Tag, Tile } from '@carbon/react';
import './notifications-panel.scss';

export interface NotificationRowProps {
  /** Short, client-safe headline for the notification. */
  title: string;
  /** Optional client-safe body text. Null/undefined renders nothing. */
  body?: string | null;
  /** Notification category (e.g. "approval", "report"). Used only for a tone. */
  kind: string;
  /** Whether the notification is unread; drives the status Tag tone. */
  unread: boolean;
  /** Optional display timestamp (already-formatted client-safe string). */
  timestamp?: string;
  /** Optional click handler; when provided the row becomes a ClickableTile. */
  onClick?: () => void;
  /** Optional action affordances (OverflowMenu, Approve/Reject, Delegate-up…). */
  actions?: ReactNode;
}

/** Safe Carbon Tag tones only — never green/red here to avoid status confusion. */
type SafeTagType = 'blue' | 'cool-gray' | 'teal' | 'purple' | 'cyan';

/** Map a free-form kind to a stable, safe Tag tone (read state wins for unread). */
function kindTone(kind: string): SafeTagType {
  switch (kind.toLowerCase()) {
    case 'approval':
    case 'action':
      return 'purple';
    case 'report':
      return 'teal';
    case 'system':
      return 'cyan';
    default:
      return 'cool-gray';
  }
}

function RowContent({ title, body, kind, unread, timestamp, actions }: NotificationRowProps) {
  const tagType: SafeTagType = unread ? 'blue' : kindTone(kind);
  const tagLabel = unread ? 'unread' : kind;
  return (
    <div className="notification-item">
      <div className="notification-item__head">
        <p className="notification-item__title">{title}</p>
        <Tag type={tagType} size="sm">
          {tagLabel}
        </Tag>
      </div>
      {body ? <p className="notification-item__body">{body}</p> : null}
      <div className="notification-item__meta">
        {timestamp ? <span className="notification-item__time">{timestamp}</span> : null}
        {actions ? <span className="notification-item__actions">{actions}</span> : null}
      </div>
    </div>
  );
}

export function NotificationRow(props: NotificationRowProps) {
  const { onClick, title, actions } = props;
  // When the row has action affordances, prefer a plain Tile so clicks
  // don't fire on every interaction with the menu / buttons.
  if (actions) {
    return (
      <Tile className="notification-item notification-item--with-actions" aria-label={title}>
        <RowContent {...props} />
      </Tile>
    );
  }
  if (onClick) {
    return (
      <ClickableTile
        className="notification-item"
        onClick={onClick}
        aria-label={`Open notification: ${title}`}
      >
        <RowContent {...props} />
      </ClickableTile>
    );
  }
  return (
    <Tile className="notification-item">
      <RowContent {...props} />
    </Tile>
  );
}
